import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useWatiSocket } from "./WatiSocketContext";
import { useCommonApiCall } from "../Api/commonApiCall";
import watiNotificationSound from "../../assets/sounds/wati_notification.wav";

const WatiChatContext = createContext();

export const useWatiChat = () => {
    const context = useContext(WatiChatContext);
    if (!context) {
        throw new Error("useWatiChat must be used within a WatiChatProvider");
    }
    return context;
};

export const WatiChatProvider = ({ children }) => {
    const { watiChatUpdates } = useWatiSocket();
    const { CommonApiCall } = useCommonApiCall();
    const navigate = useNavigate();
    const location = useLocation();

    const [chatlist, setChatlist] = useState([]);
    const [chatlistLoading, setChatlistLoading] = useState(true);
    const [continuationToken, setContinuationToken] = useState(null);

    const watiProcessedCountRef = useRef(0);
    // WhatsappMessages writes the currently open chat ID here so the
    // socket handler can keep unreadCount at 0 for that chat.
    const activeChatIdRef = useRef(null);
    const notificationAudioRef = useRef(new Audio(watiNotificationSound));

    const getAllChats = async () => {
        setChatlistLoading(true);
        try {
            const response = await CommonApiCall(
                { api_key: "uzdpart" },
                "wati_messages/fetch_messages",
                "post"
            );
            if (response.status) {
                setChatlist(response.data.final_data);
                setContinuationToken(response.data.continuationToken);
            }
        } catch (_) {
            // silent — WhatsappMessages shows error toasts for user-triggered reloads
        } finally {
            setChatlistLoading(false);
        }
    };

    // Load chatlist globally on app start
    useEffect(() => {
        getAllChats();
    }, []);

    // Process incoming socket events — chatlist upsert only
    useEffect(() => {
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;

        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        newUpdates.forEach(update => {
            if (
                update.target === "sendTextMessage" &&
                update["0"]?.message?.conViewModel?.channelId === "68d251023d3de3334068a1d9"
            ) {
                const conv = update["0"].message.conViewModel;
                const isOpen = activeChatIdRef.current === update["0"].conversationId;

                const updatedEntry = {
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: conv.text,
                    unreadCount: isOpen ? 0 : conv.unreadCount,
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                };

                setChatlist(prev => {
                    const exists = prev.some(c => c.id === updatedEntry.id);
                    const next = exists
                        ? prev.map(c => c.id === updatedEntry.id ? updatedEntry : c)
                        : [...prev, updatedEntry];
                    return [...next].sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated));
                });

                if (update["0"].message.isOwner === false) {
                    notificationAudioRef.current.currentTime = 0;
                    notificationAudioRef.current.play().catch(() => {});

                    if (location.pathname !== "/whatsapp_messages") {
                        toast.info(
                            <p style={{ fontSize: 14 }}>
                                💬 <strong>{conv.name}</strong>: {conv.text}
                            </p>,
                            {
                                containerId: "wati-global",
                                position: "bottom-left",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                onClick: () => navigate("/whatsapp_messages"),
                                style: { cursor: "pointer" },
                            }
                        );
                    }
                }
            }
        });
    }, [watiChatUpdates]);

    const totalUnread = chatlist.filter(c => c.unreadCount > 0).length;

    return (
        <WatiChatContext.Provider value={{
            chatlist,
            setChatlist,
            chatlistLoading,
            setChatlistLoading,
            continuationToken,
            setContinuationToken,
            getAllChats,
            activeChatIdRef,
            totalUnread,
        }}>
            <ToastContainer containerId="wati-global" />
            {children}
        </WatiChatContext.Provider>
    );
};
